import {
  type MatrixName,
  substitutionTable,
} from "@/lib/tool-kit/biology-genetics/substitution-matrices";

/**
 * Pairwise sequence alignment: Needleman–Wunsch (global) and Smith–Waterman (local) with
 * affine gap penalties, following Gotoh's three-state recurrence.
 *
 * A gap of length k costs `gapOpen + (k − 1) × gapExtend` (the convention of EMBOSS Needle and
 * Biopython's PairwiseAligner); set the two equal for a linear gap penalty. Penalties are
 * entered as positive numbers. The optimal score is exact; when several alignments tie, the
 * traceback prefers a match or mismatch over a gap in the first sequence, then a gap in the
 * second, so it returns one of them.
 */

export type AlignMode = "global" | "local";

export interface Scoring {
  /** Score for aligning residue `a` of the first sequence with `b` of the second. */
  pair: (a: string, b: string) => number;
  gapOpen: number;
  gapExtend: number;
}

export interface Alignment {
  score: number;
  alignedA: string;
  alignedB: string;
  /** 0-based start and exclusive end in each input sequence. */
  startA: number;
  endA: number;
  startB: number;
  endB: number;
}

export function dnaScoring(
  match: number,
  mismatch: number,
  gapOpen: number,
  gapExtend: number
): Scoring {
  return { pair: (a, b) => (a === b ? match : mismatch), gapOpen, gapExtend };
}

export function proteinScoring(
  matrix: MatrixName,
  gapOpen: number,
  gapExtend: number
): Scoring {
  const table = substitutionTable(matrix);
  return {
    pair: (a, b) =>
      table[a + b] ??
      table[`${a === "*" ? "*" : "X"}${b === "*" ? "*" : "X"}`] ??
      0,
    gapOpen,
    gapExtend,
  };
}

/** States of the Gotoh recurrence. */
const START = 0;
const IN_M = 1;
const IN_X = 2;
const IN_Y = 3;

interface Cell {
  value: number;
  from: number;
}

function best3(m: number, x: number, y: number): Cell {
  if (m >= x && m >= y) {
    return { value: m, from: IN_M };
  }
  return x >= y ? { value: x, from: IN_X } : { value: y, from: IN_Y };
}

/** Largest number of cells (rows × columns) the aligner will fill. */
export const MAX_CELLS = 12_000_000;

interface Fill {
  pointers: Uint8Array;
  endI: number;
  endJ: number;
  endState: number;
  score: number;
}

/** Three 2-bit predecessor states packed into one byte, without bitwise operators. */
function pack(m: number, x: number, y: number): number {
  return m + x * 4 + y * 16;
}

function unpack(code: number, state: number): number {
  if (state === IN_M) {
    return code % 4;
  }
  return state === IN_X ? Math.floor(code / 4) % 4 : Math.floor(code / 16);
}

interface Rows {
  m: Float64Array;
  x: Float64Array;
  y: Float64Array;
}

function newRows(width: number): Rows {
  return {
    m: new Float64Array(width).fill(Number.NEGATIVE_INFINITY),
    x: new Float64Array(width).fill(Number.NEGATIVE_INFINITY),
    y: new Float64Array(width).fill(Number.NEGATIVE_INFINITY),
  };
}

/** One row of the recurrence; returns the best local cell in this row, if any. */
function fillRow(
  prev: Rows,
  cur: Rows,
  residue: string,
  b: string,
  scoring: Scoring,
  local: boolean,
  pointers: Uint8Array,
  rowStart: number
): { score: number; column: number } {
  const { gapOpen: open, gapExtend: extend } = scoring;
  let rowBest = Number.NEGATIVE_INFINITY;
  let rowColumn = 0;
  for (let j = 1; j <= b.length; j++) {
    const diagonal = best3(prev.m[j - 1], prev.x[j - 1], prev.y[j - 1]);
    const restart = local && diagonal.value <= 0;
    cur.m[j] = (restart ? 0 : diagonal.value) + scoring.pair(residue, b[j - 1]);
    const up = best3(prev.m[j] - open, prev.x[j] - extend, prev.y[j] - open);
    cur.x[j] = up.value;
    const left = best3(
      cur.m[j - 1] - open,
      cur.x[j - 1] - open,
      cur.y[j - 1] - extend
    );
    cur.y[j] = left.value;
    pointers[rowStart + j] = pack(
      restart ? START : diagonal.from,
      up.from,
      left.from
    );
    if (cur.m[j] > rowBest) {
      rowBest = cur.m[j];
      rowColumn = j;
    }
  }
  return { score: rowBest, column: rowColumn };
}

function fill(a: string, b: string, mode: AlignMode, scoring: Scoring): Fill {
  const n = a.length;
  const m = b.length;
  const width = m + 1;
  const local = mode === "local";
  const pointers = new Uint8Array((n + 1) * width);
  let prev = newRows(width);
  let cur = newRows(width);
  prev.m[0] = 0;
  for (let j = 1; j <= m; j++) {
    prev.y[j] = -scoring.gapOpen - (j - 1) * scoring.gapExtend;
    pointers[j] = pack(START, START, j === 1 ? IN_M : IN_Y);
    if (local) {
      prev.m[j] = 0;
    }
  }
  let bestScore = Number.NEGATIVE_INFINITY;
  let bestI = 0;
  let bestJ = 0;
  for (let i = 1; i <= n; i++) {
    cur.m[0] = local ? 0 : Number.NEGATIVE_INFINITY;
    cur.x[0] = -scoring.gapOpen - (i - 1) * scoring.gapExtend;
    cur.y[0] = Number.NEGATIVE_INFINITY;
    pointers[i * width] = pack(START, i === 1 ? IN_M : IN_X, START);
    const row = fillRow(
      prev,
      cur,
      a[i - 1],
      b,
      scoring,
      local,
      pointers,
      i * width
    );
    if (local && row.score > bestScore) {
      bestScore = row.score;
      bestI = i;
      bestJ = row.column;
    }
    [prev, cur] = [cur, prev];
  }
  if (local) {
    return {
      pointers,
      endI: bestI,
      endJ: bestJ,
      endState: IN_M,
      score: Math.max(bestScore, 0),
    };
  }
  const end = best3(prev.m[m], prev.x[m], prev.y[m]);
  return { pointers, endI: n, endJ: m, endState: end.from, score: end.value };
}

/** Align two sequences; null when either is empty or the problem is too large. */
export function align(
  a: string,
  b: string,
  mode: AlignMode,
  scoring: Scoring
): Alignment | null {
  if (
    a.length === 0 ||
    b.length === 0 ||
    (a.length + 1) * (b.length + 1) > MAX_CELLS
  ) {
    return null;
  }
  const width = b.length + 1;
  const { pointers, endI, endJ, endState, score } = fill(a, b, mode, scoring);
  if (mode === "local" && score <= 0) {
    return {
      score: 0,
      alignedA: "",
      alignedB: "",
      startA: 0,
      endA: 0,
      startB: 0,
      endB: 0,
    };
  }
  const topRow: string[] = [];
  const bottomRow: string[] = [];
  let i = endI;
  let j = endJ;
  let state = endState;
  while (i > 0 || j > 0) {
    const code = pointers[i * width + j];
    if (state === IN_M) {
      topRow.push(a[i - 1]);
      bottomRow.push(b[j - 1]);
      state = unpack(code, IN_M);
      i -= 1;
      j -= 1;
      if (state === START) {
        break;
      }
    } else if (state === IN_X) {
      topRow.push(a[i - 1]);
      bottomRow.push("-");
      state = unpack(code, IN_X);
      i -= 1;
    } else {
      topRow.push("-");
      bottomRow.push(b[j - 1]);
      state = unpack(code, IN_Y);
      j -= 1;
    }
  }
  return {
    score,
    alignedA: topRow.reverse().join(""),
    alignedB: bottomRow.reverse().join(""),
    startA: i,
    endA: endI,
    startB: j,
    endB: endJ,
  };
}

/** Re-score a finished alignment column by column; used to check the DP. */
export function scoreAlignment(
  alignedA: string,
  alignedB: string,
  scoring: Scoring
): number {
  let total = 0;
  let gapA = 0;
  let gapB = 0;
  for (let k = 0; k < alignedA.length; k++) {
    const x = alignedA[k];
    const y = alignedB[k];
    if (x === "-") {
      total -= gapA === 0 ? scoring.gapOpen : scoring.gapExtend;
      gapA += 1;
      gapB = 0;
    } else if (y === "-") {
      total -= gapB === 0 ? scoring.gapOpen : scoring.gapExtend;
      gapB += 1;
      gapA = 0;
    } else {
      total += scoring.pair(x, y);
      gapA = 0;
      gapB = 0;
    }
  }
  return total;
}

export interface AlignmentStats {
  columns: number;
  identities: number;
  /** Non-identical columns with a positive score (protein matrices). */
  positives: number;
  mismatches: number;
  gaps: number;
  gapOpenings: number;
}

export function alignmentStats(
  alignedA: string,
  alignedB: string,
  scoring: Scoring
): AlignmentStats {
  const stats: AlignmentStats = {
    columns: alignedA.length,
    identities: 0,
    positives: 0,
    mismatches: 0,
    gaps: 0,
    gapOpenings: 0,
  };
  let inGapA = false;
  let inGapB = false;
  for (let k = 0; k < alignedA.length; k++) {
    const x = alignedA[k];
    const y = alignedB[k];
    if (x === "-" || y === "-") {
      stats.gaps += 1;
      const openedA = x === "-" && !inGapA;
      const openedB = y === "-" && !inGapB;
      if (openedA || openedB) {
        stats.gapOpenings += 1;
      }
      inGapA = x === "-";
      inGapB = y === "-";
      continue;
    }
    inGapA = false;
    inGapB = false;
    if (x === y) {
      stats.identities += 1;
    } else if (scoring.pair(x, y) > 0) {
      stats.positives += 1;
    } else {
      stats.mismatches += 1;
    }
  }
  return stats;
}

export type MatchSymbol = "|" | ":" | "." | " ";

/** The line between the two rows: | identical, : similar, . different, blank for a gap. */
export function matchLine(
  alignedA: string,
  alignedB: string,
  scoring: Scoring,
  protein: boolean
): string {
  let line = "";
  for (let k = 0; k < alignedA.length; k++) {
    const x = alignedA[k];
    const y = alignedB[k];
    if (x === "-" || y === "-") {
      line += " ";
    } else if (x === y) {
      line += "|";
    } else if (protein && scoring.pair(x, y) > 0) {
      line += ":";
    } else {
      line += ".";
    }
  }
  return line;
}

export interface AlignmentBlock {
  top: string;
  middle: string;
  bottom: string;
  /** 1-based positions of the first and last residue of each row in this block (0 when only gaps). */
  topFrom: number;
  topTo: number;
  bottomFrom: number;
  bottomTo: number;
}

/** Split the alignment into blocks of `width` columns with running residue numbers. */
export function alignmentBlocks(
  result: Alignment,
  scoring: Scoring,
  protein: boolean,
  width = 60
): AlignmentBlock[] {
  const middle = matchLine(result.alignedA, result.alignedB, scoring, protein);
  const blocks: AlignmentBlock[] = [];
  let posA = result.startA;
  let posB = result.startB;
  for (let start = 0; start < result.alignedA.length; start += width) {
    const top = result.alignedA.slice(start, start + width);
    const bottom = result.alignedB.slice(start, start + width);
    const countA = [...top].filter((c) => c !== "-").length;
    const countB = [...bottom].filter((c) => c !== "-").length;
    blocks.push({
      top,
      bottom,
      middle: middle.slice(start, start + width),
      topFrom: countA > 0 ? posA + 1 : 0,
      topTo: countA > 0 ? posA + countA : 0,
      bottomFrom: countB > 0 ? posB + 1 : 0,
      bottomTo: countB > 0 ? posB + countB : 0,
    });
    posA += countA;
    posB += countB;
  }
  return blocks;
}

/**
 * The classic score matrix for a linear gap penalty, for teaching on short sequences.
 * Row i, column j holds the best score of an alignment ending at a[0..i), b[0..j).
 */
export function linearMatrix(
  a: string,
  b: string,
  mode: AlignMode,
  scoring: Scoring
): number[][] {
  const gap = scoring.gapExtend;
  const local = mode === "local";
  const grid: number[][] = [];
  for (let i = 0; i <= a.length; i++) {
    const row: number[] = [];
    for (let j = 0; j <= b.length; j++) {
      if (i === 0 || j === 0) {
        row.push(local ? 0 : 0 - gap * (i + j));
        continue;
      }
      const value = Math.max(
        grid[i - 1][j - 1] + scoring.pair(a[i - 1], b[j - 1]),
        grid[i - 1][j] - gap,
        row[j - 1] - gap
      );
      row.push(local ? Math.max(0, value) : value);
    }
    grid.push(row);
  }
  return grid;
}

/** The matrix cells (row, column) the alignment passes through, for highlighting. */
export function pathCells(result: Alignment): Set<string> {
  const cells = new Set<string>();
  let i = result.startA;
  let j = result.startB;
  cells.add(`${i},${j}`);
  for (let k = 0; k < result.alignedA.length; k++) {
    if (result.alignedA[k] !== "-") {
      i += 1;
    }
    if (result.alignedB[k] !== "-") {
      j += 1;
    }
    cells.add(`${i},${j}`);
  }
  return cells;
}

/** The alignment as plain text in EMBOSS-like blocks, for the page and for copying. */
export function formatAlignment(
  result: Alignment,
  names: readonly [string, string],
  scoring: Scoring,
  protein: boolean,
  width = 60
): string {
  const blocks = alignmentBlocks(result, scoring, protein, width);
  const label = Math.min(14, Math.max(names[0].length, names[1].length, 1));
  const digits = Math.max(
    String(result.endA).length,
    String(result.endB).length,
    1
  );
  const pad = (name: string) => name.slice(0, label).padEnd(label);
  const number = (value: number) =>
    (value === 0 ? "" : String(value)).padStart(digits);
  const blank = " ".repeat(label + 1 + digits + 1);
  const lines: string[] = [];
  for (const block of blocks) {
    lines.push(
      `${pad(names[0])} ${number(block.topFrom)} ${block.top} ${number(block.topTo).trim()}`
    );
    lines.push(`${blank}${block.middle}`);
    lines.push(
      `${pad(names[1])} ${number(block.bottomFrom)} ${block.bottom} ${number(block.bottomTo).trim()}`
    );
    lines.push("");
  }
  return lines.join("\n").trimEnd();
}
